import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';

// Material UI Icons
import TuneIcon from '@mui/icons-material/Tune';
import SaveIcon from '@mui/icons-material/Save';
import InfoIcon from '@mui/icons-material/Info';
import CancelIcon from '@mui/icons-material/Cancel';

import { useParameter } from '../context/ParameterContext';
import Button from '../components/formElements/Button';

interface ParameterFormData {
  name: string;
  description?: string;
}

interface ParameterFormProps {
  categoryId?: number;
  id?: number;
  name?: string;
  description?: string;
  inEditMode?: boolean;
}

function ParameterForm({
  categoryId,
  id,
  name = '',
  description = '',
  inEditMode = false,
}: ParameterFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
  } = useForm<ParameterFormData>({
    defaultValues: {
      name,
      description,
    },
  });

  const { createParameter, editParameter } = useParameter();
  const navigate = useNavigate();

  const onSubmit = async (data: ParameterFormData) => {
    setIsSubmitting(true);

    const toastId = toast.loading(
      inEditMode ? 'Aktualizowanie parametru...' : 'Dodawanie parametru...',
      {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: 0,
        theme: 'colored',
      },
    );

    try {
      if (inEditMode && id) {
        await editParameter(id, data.name, data.description);
        toast.update(toastId, {
          render: 'Parametr został zaktualizowany pomyślnie!',
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        });
      } else {
        await createParameter(data.name, data.description, categoryId);
        toast.update(toastId, {
          render: 'Parametr został dodany pomyślnie!',
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        });
      }

      // Nawigacja z powrotem do odpowiedniej ścieżki
      if (categoryId) {
        navigate(`/admin/parametry/${categoryId}`);
      } else {
        navigate('/admin/parametry');
      }
    } catch (error: Error | any) {
      console.error('Błąd podczas zapisywania parametru:', error);

      if (
        error?.message ===
        'Parameter already exists in this category or ancestors'
      ) {
        toast.update(toastId, {
          render:
            'Parametr o tej nazwie już istnieje w tej kategorii lub w kategorii nadrzędnej',
          type: 'error',
          isLoading: false,
          autoClose: 5000,
        });
      } else {
        toast.update(toastId, {
          render:
            error.message ||
            (inEditMode
              ? 'Nie udało się zaktualizować parametru'
              : 'Nie udało się dodać parametru'),
          type: 'error',
          isLoading: false,
          autoClose: 3000,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="p-6">
        <div className="mb-6 pb-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <TuneIcon className="text-primary" />
            <h2 className="text-xl font-medium text-gray-900">
              {inEditMode ? 'Edytuj parametr' : 'Nowy parametr'}
            </h2>
          </div>
          <p className="text-gray-500 text-sm mt-1">
            {categoryId
              ? 'Parametr dla konkretnej kategorii i jej podkategorii'
              : 'Parametr globalny dostępny dla wszystkich kategorii'}
          </p>
        </div>

        <div className="space-y-6">
          {/* Nazwa parametru */}
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Nazwa parametru *
            </label>
            <Controller
              name="name"
              control={control}
              rules={{
                required: 'Nazwa parametru jest wymagana',
                minLength: {
                  value: 2,
                  message: 'Nazwa musi mieć co najmniej 2 znaki',
                },
              }}
              render={({ field }) => (
                <input
                  {...field}
                  id="name"
                  placeholder="Np. Kolor, Rozmiar, Moc"
                  className={`w-full px-3 py-2 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-primary focus:border-primary`}
                />
              )}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>

          {/* Opis parametru */}
          <div>
            <label
              htmlFor="description"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Opis parametru{' '}
              <span className="text-gray-400 text-xs">(opcjonalny)</span>
            </label>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <textarea
                  {...field}
                  id="description"
                  rows={4}
                  placeholder="Dodatkowy opis parametru lub instrukcje dla sprzedawców"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                />
              )}
            />
          </div>

          {/* Panel informacyjny */}
          <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-4">
            <div className="flex">
              <InfoIcon className="text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-yellow-800">
                  Wskazówki dot. parametrów
                </h3>
                <ul className="mt-2 text-sm text-yellow-700 list-disc pl-5 space-y-1">
                  <li>
                    Używaj jednoznacznych nazw parametrów (np. "Rozmiar ekranu"
                    zamiast "Rozmiar")
                  </li>
                  <li>
                    Unikaj tworzenia podobnych parametrów w różnych kategoriach
                  </li>
                  <li>
                    Parametry globalne są dostępne we wszystkich kategoriach
                  </li>
                  <li>
                    Parametry dziedziczone są automatycznie przez podkategorie
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Przyciski formularza */}
        <div className="mt-8 flex justify-end space-x-4 pt-4 border-t border-gray-200">
          <Button
            type="button"
            colorTheme="outline"
            onClick={() =>
              categoryId
                ? navigate(`/admin/parametry/${categoryId}`)
                : navigate('/admin/parametry')
            }
            className="w-auto px-4"
          >
            <CancelIcon fontSize="small" className="mr-1" />
            Anuluj
          </Button>

          <Button
            type="submit"
            colorTheme="primary"
            className="w-auto px-4"
            disabled={isSubmitting || (!isDirty && inEditMode)}
          >
            <SaveIcon fontSize="small" className="mr-1" />
            {isSubmitting
              ? inEditMode
                ? 'Aktualizowanie...'
                : 'Dodawanie...'
              : inEditMode
                ? 'Zaktualizuj parametr'
                : 'Dodaj parametr'}
          </Button>
        </div>
      </form>
    </motion.div>
  );
}

export default ParameterForm;
